import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableFooter,
} from "@mui/material";
import {
  HoldingsPageShell,
  SortableHeader,
  EmptyTableRow,
  centerBodyCellSx,
  centerHeadCellSx,
  gainLossCellSx,
  holdingsPaginationSx,
} from "./holdingsTableShared";

interface Stock {
  id: number;
  symbol:string;
  transactionDate?: string;
  createdAt?: string;
  units:number;
  purchasePrice:number;
  amount:number;
  currentAmount:number;
  currentPrice:number;
  gainLoss:number;
  gainLossPerc:number;
}

interface Props {
  data: Stock[];
  totalPages: number; // Total number of users
  pageSize: number; // Current page size
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void; // Page change handler
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void; // Page size change handler
  currentPage: number; // Current page number
  fetchData: () => void;
}

const StockHoldingsTable: React.FC<Props> = ({
  data,
  totalPages,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  fetchData,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState<Stock[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Stock | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // const location = useLocation();
  // let selectedData = location?.state?.selectedData||{};

  // console.log("this is current page", selectedData);

  console.log("this is current page", data);
  console.log("this is page size", pageSize);

  const isEmptyObject = (obj: object): boolean => {
    return Object.keys(obj).length === 0;
  };

  useEffect(() => {
    // Filter and sort data whenever the data or search term changes
    let updatedData = data;

    // Search functionality
    if (searchTerm) {
      updatedData = updatedData?.filter((row) =>
        Object.values(row).some(
          (value) =>
            value != null &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Sorting functionality
    if (sortColumn) {
      updatedData = [...updatedData].sort((a, b) => {
        const aVal = a[sortColumn] ?? "";
        const bVal = b[sortColumn] ?? "";
        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(updatedData);
  }, [data, searchTerm, sortColumn, sortOrder]);

  const handleSort = (column: keyof Stock) => {
    setSortColumn(column);
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const formatTransactionDate = (value?: string | number): string => {
    if (value == null || value === "") return "-";
    if (typeof value === "string") {
      return value.includes("/") ? value.replace(/\//g, "-") : value;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const pagedData = filteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? [];

  const sortHead = (label: string, column: keyof Stock) => (
    <TableCell sx={centerHeadCellSx}>
      <SortableHeader label={label} onSort={() => handleSort(column)} align="center" />
    </TableCell>
  );

  return (
    <HoldingsPageShell
      title="Equity"
      subtitle="View equity holdings for this investor."
      searchValue={searchTerm}
      onSearchChange={(value) => {
        setSearchTerm(value);
        setPage(0);
      }}
      searchPlaceholder="Search equity holdings..."
    >
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {sortHead("Symbol", "symbol")}
              {sortHead("Transaction Date", "transactionDate")}
              {sortHead("Units", "units")}
              {sortHead("Amount", "amount")}
              {sortHead("Purchase Price", "purchasePrice")}
              {sortHead("Current Amount", "currentAmount")}
              {sortHead("Current Price", "currentPrice")}
              {sortHead("Gain/Loss", "gainLoss")}
              {sortHead("Gain/Loss (%)", "gainLossPerc")}
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedData.length === 0 ? (
              <TableRow>
                <EmptyTableRow colSpan={9} message="No equity holdings found" />
              </TableRow>
            ) : (
              pagedData.map((row) => (
                <TableRow key={row.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                  <TableCell sx={centerBodyCellSx}>{row?.symbol}</TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {formatTransactionDate(row?.transactionDate)}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {Number(row?.units)?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {"Rs." + Number(row?.amount)?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {"Rs." + Number(row?.purchasePrice)?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {"Rs." + Number(row?.currentAmount)?.toFixed(2)}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {"Rs." + Number(row?.currentPrice)?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell sx={gainLossCellSx(row?.gainLoss)}>
                    {"Rs." + Number(row?.gainLoss)?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell sx={gainLossCellSx(row?.gainLossPerc)}>
                    {Number(row?.gainLossPerc)?.toFixed(2) || "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onPageSizeChange}
                sx={holdingsPaginationSx}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </HoldingsPageShell>
  );
};

export default StockHoldingsTable;
